import type { GridApi, StateUpdatedEvent } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useBlotterTableStorage, type BlotterTableState } from './BlotterTableStorageContext';
import {
  filterExtraGridStateForReadWrite,
  filterGridStateForReadWrite,
  getDefaultedOptions,
  type UsePersistedBlotterTableOptions,
} from './usePersistedBlotterTableUtils';
import { usePersistedColumnDynamics, type UsePersistedColumnDynamicsOutput } from './usePersistedColumnDynamics';
import {
  usePersistedRowGroupExpansion,
  type UsePersistedRowGroupExpansionOutput,
} from './usePersistedRowGroupExpansion';

import { useConstant } from '../../hooks';
import { EMPTY_OBJECT } from '../../utils';
import type { Column } from './columns/types';
import type { BlotterTableFilter } from './types';
export {
  filterExtraGridStateForReadWrite,
  filterGridStateForReadWrite,
  getDefaultedOptions,
} from './usePersistedBlotterTableUtils';
export type {
  DefaultedUsePersistedBlotterTableOptions,
  UsePersistedBlotterTableOptions,
} from './usePersistedBlotterTableUtils';

export type UsePersistedColumnsChangedParams<R = any> = {
  columns: Column<R>[];
  api: GridApi<R>;
};

export type UsePersistedSortChangedParams<R = any> = {
  api: GridApi<R>;
};

export type UsePersistedBlotterTable = {
  /** Resolved persisted slice: grid state, filter, and Talos-only metadata. */
  state: BlotterTableState | undefined;
  /** First-render snapshot of {@link UsePersistedBlotterTable.state}. */
  initialState: BlotterTableState | undefined;
  /** Callback for useBlotterTable on grid state updates; persists grid (and expansion handling). */
  eventHandlers: {
    onStateUpdated: (event: StateUpdatedEvent) => void;
  } & UsePersistedRowGroupExpansionOutput['eventHandlers'] &
    UsePersistedColumnDynamicsOutput['eventHandlers'];
  onFilterChanged(filters?: BlotterTableFilter): void;
};

/**
 * Store blotter state in persistent storage.
 * @param blotterID Storage key; when null, grid state is not persisted and `gridState` is omitted from the returned slice.
 * @param initialState Defaults used when storage has no value for a field.
 * @param options e.g. {@link UsePersistedBlotterTableOptions.persistFilter}
 */
export function usePersistedBlotterTable(
  blotterID: string | null,
  inputInitialState?: BlotterTableState,
  options: UsePersistedBlotterTableOptions = EMPTY_OBJECT
): UsePersistedBlotterTable {
  const {
    persistFilter: inputPersistFilter,
    persistGridState,
    persistColumns,
    persistCellSelection,
    persistRowSelection,
    persistRowGroupExpansion,
    persistScroll,
  } = options;
  const defaultedOptions = useMemo(
    () =>
      getDefaultedOptions({
        persistFilter: inputPersistFilter,
        persistGridState,
        persistColumns,
        persistCellSelection,
        persistRowSelection,
        persistRowGroupExpansion,
        persistScroll,
      }),
    [
      inputPersistFilter,
      persistGridState,
      persistColumns,
      persistCellSelection,
      persistRowSelection,
      persistRowGroupExpansion,
      persistScroll,
    ]
  );
  const { persistFilter } = defaultedOptions;
  const { getState, setGridState, setFilterState } = useBlotterTableStorage();

  const { eventHandlers: rowGroupExpansionEventHandlers, onStateUpdatedInterceptor } = usePersistedRowGroupExpansion({
    blotterID,
    options: defaultedOptions,
  });
  const { eventHandlers: columnDynamicsEventHandlers } = usePersistedColumnDynamics({
    blotterID,
    options: defaultedOptions,
  });

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      if (!blotterID) {
        return;
      }

      // "Interceptors" handle updating extra grid state, and they might modify event.state.
      const interceptedEvent = onStateUpdatedInterceptor(event);
      const filteredGridState = filterGridStateForReadWrite(interceptedEvent.state, defaultedOptions);
      if (filteredGridState === undefined) {
        return;
      }

      // Only update the state (causing an AppConfig update call) if there is an actual change. This is much cheaper than the alternative.
      const persistedGridState = getState(blotterID)?.gridState;
      if (isEqual(filteredGridState, persistedGridState)) {
        return;
      }

      setGridState(blotterID, filteredGridState);
    },
    [blotterID, defaultedOptions, getState, onStateUpdatedInterceptor, setGridState]
  );

  const state = useMemo((): BlotterTableState | undefined => {
    if (!blotterID) {
      return undefined;
    }

    const persistedState = getState(blotterID);
    const readablePersistedState: BlotterTableState | undefined =
      persistedState == null
        ? undefined
        : {
            gridState: filterGridStateForReadWrite(persistedState.gridState, defaultedOptions),
            extraGridState: filterExtraGridStateForReadWrite(persistedState.extraGridState, defaultedOptions),
            filter: persistFilter ? persistedState.filter : undefined,
          };

    return combineStates(readablePersistedState, inputInitialState);
  }, [blotterID, getState, inputInitialState, persistFilter, defaultedOptions]);

  const onFilterChanged = useCallback<UsePersistedBlotterTable['onFilterChanged']>(
    (filter?: BlotterTableFilter) => {
      if (blotterID != null && persistFilter) {
        setFilterState(blotterID, filter);
      }
    },
    [blotterID, persistFilter, setFilterState]
  );

  const initialState = useConstant(withPartialColumnState(state));

  return useMemo(
    () => ({
      state,
      initialState,
      eventHandlers: {
        onStateUpdated,
        ...rowGroupExpansionEventHandlers,
        ...columnDynamicsEventHandlers,
      },
      onFilterChanged,
    }),
    [state, initialState, onStateUpdated, rowGroupExpansionEventHandlers, columnDynamicsEventHandlers, onFilterChanged]
  );
}

/**
 * We always combine states between persisted and defaults. Our system can be persisting just a gridState, for example, but not filter, despite there being a default filter.
 * In these cases, we want to make sure to pass along the default filter.
 */
function combineStates(
  persistedState: BlotterTableState | undefined,
  inputInitialState: BlotterTableState | undefined
): BlotterTableState | undefined {
  if (!persistedState && !inputInitialState) {
    return undefined;
  }

  return {
    gridState: persistedState?.gridState ?? inputInitialState?.gridState,
    extraGridState: persistedState?.extraGridState ?? inputInitialState?.extraGridState,
    filter: persistedState?.filter ?? inputInitialState?.filter,
  };
}

function withPartialColumnState(state: BlotterTableState | undefined): BlotterTableState | undefined {
  if (state?.gridState == null) {
    return state;
  }

  return {
    ...state,
    gridState: {
      ...state.gridState,
      // We intentionally persist only selected column-state slices. Tell AG Grid not to treat missing slices
      // (for example aggregation, row grouping, pivoting, or future column state) as explicit resets.
      partialColumnState: true,
    },
  };
}
